
import React, { useRef, useCallback } from 'react';
import { useDroppable, useDndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Card } from './Card';
import { Employee } from '../types';

interface ColumnProps {
  id: string;
  title: string;
  color?: string;
  items: Employee[];
  roleColors: Record<string, string>;
  isHighlighted?: boolean;
  isStacked?: boolean;
  /** Empilhamento compacto: altura pelo conteúdo, gaps menores (Sanplast). */
  compactStack?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onAdd?: () => void;
  onEdit?: (employee: Employee) => void;
  onDelete?: (id: string) => void;
  /** Permite arrastar cards entre colunas (requer permissão de editar colaborador). */
  canDragEmployees?: boolean;
  /** Data de referência para indicador de faltas no card. */
  referenceDate?: Date;
}

/** Altura inicial estimada por card (px) até measureElement calibrar. */
const CARD_ESTIMATE_PX = 58;

/** Só virtualiza listas longas fora do modo compacto (Sanplast usa scroll nativo). */
const VIRTUALIZE_MIN_ITEMS = 12;

/**
 * Renderiza cards de colaboradores; virtualiza listas longas quando não há drag ativo.
 */
const ColumnComponent: React.FC<ColumnProps> = ({
  id,
  title,
  color = '#6B7280',
  items,
  roleColors,
  isHighlighted = false,
  isStacked = false,
  compactStack = false,
  isCollapsed = false,
  onToggleCollapse,
  onAdd,
  onEdit,
  onDelete,
  canDragEmployees = true,
  referenceDate,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { setNodeRef } = useDroppable({ id });
  const { active } = useDndContext();

  /**
   * Une ref do droppable com ref de scroll do virtualizer.
   * @param node - Elemento scrollável da coluna
   */
  const setScrollContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      scrollRef.current = node;
    },
    [setNodeRef],
  );

  const shouldVirtualize =
    !isCollapsed &&
    !compactStack &&
    items.length >= VIRTUALIZE_MIN_ITEMS &&
    active == null;

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => CARD_ESTIMATE_PX,
    overscan: 4,
    enabled: shouldVirtualize,
  });

  /** Espaçamento uniforme entre cards — igual em todos os setores Sanplast. */
  const cardGapClass = compactStack ? 'gap-1' : 'gap-2';
  const cardPadClass = compactStack ? 'p-1' : 'p-1.5';

  /**
   * Renderiza um card de colaborador.
   * @param employee - Dados do colaborador
   */
  const renderCard = (employee: Employee) => (
    <Card
      key={employee.id}
      employee={employee}
      roleColor={roleColors[employee.role]}
      onEdit={onEdit}
      onDelete={onDelete}
      canDrag={canDragEmployees}
      referenceDate={referenceDate}
    />
  );

  return (
    <div
      className={`flex flex-col shrink-0 ${compactStack ? 'gap-1' : 'gap-2'} transition-all duration-300 ease-in-out
      ${isCollapsed ? 'w-[44px]' : 'w-[264px]'} 
      ${isStacked ? 'flex-1 min-h-0' : 'h-full'}`}
    >
      <div className={`flex items-center justify-between px-2 py-0.5 shrink-0 ${isCollapsed ? 'flex-col gap-3 py-2' : ''}`}>
        {!isCollapsed ? (
          <>
            <div className="flex items-center gap-2 overflow-hidden">
              <h3 className="font-semibold text-[12px] tracking-tight truncate max-w-[180px]" style={{ color: color }}>{title}</h3>
              <span
                className="px-1.5 py-0.5 rounded-full text-[9px] font-bold shrink-0"
                style={{ backgroundColor: `${color}15`, color: color }}
              >
                {items.length}
              </span>
            </div>
            <button
              onClick={onToggleCollapse}
              className="text-slate-300 hover:text-slate-500 transition-colors p-0.5 rounded-full hover:bg-slate-100"
            >
              <span className="material-symbols-outlined !text-[14px]">more_horiz</span>
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onToggleCollapse}
              className="text-slate-300 hover:text-slate-500 transition-colors p-0.5 rounded-full hover:bg-slate-100"
            >
              <span className="material-symbols-outlined !text-[14px]">more_horiz</span>
            </button>
            <div className="flex flex-col items-center gap-3">
              <span
                className="px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                style={{ backgroundColor: `${color}15`, color: color }}
              >
                {items.length}
              </span>
              <h3
                className="font-semibold text-[10px] whitespace-nowrap rotate-180"
                style={{ color: color, writingMode: 'vertical-rl' }}
              >
                {title}
              </h3>
            </div>
          </>
        )}
      </div>

      <div
        ref={setScrollContainerRef}
        className={`flex flex-col ${cardGapClass} ${cardPadClass} rounded-lg transition-all duration-300
        ${isStacked && compactStack
            ? 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden border-slate-200 bg-slate-50/50'
            : isStacked
            ? 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden border-slate-200 bg-slate-50/50'
            : 'flex-1 min-h-[120px] overflow-y-auto overflow-x-hidden bg-[#F1F5F9]/50 border-slate-200 border-dashed'
          } border ${isHighlighted ? 'bg-secondary-accent/5 ring-1 ring-secondary-accent/20' : ''}
        ${isCollapsed ? 'items-center opacity-60 bg-slate-200/20 overflow-hidden' : ''} custom-scrollbar`}
      >
        {!isCollapsed && (
          <SortableContext
            id={id}
            items={items.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-4 text-slate-300 gap-1.5 opacity-50">
                <span className="material-symbols-outlined !text-[18px]">move_to_inbox</span>
                <p className="text-[8px] font-bold uppercase tracking-widest">Vazio</p>
              </div>
            ) : shouldVirtualize ? (
              <div
                className="relative w-full"
                style={{ height: `${virtualizer.getTotalSize()}px` }}
              >
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const employee = items[virtualRow.index];
                  return (
                    <div
                      key={virtualRow.key}
                      data-index={virtualRow.index}
                      ref={virtualizer.measureElement}
                      className="absolute left-0 top-0 w-full pb-2"
                      style={{ transform: `translateY(${virtualRow.start}px)` }}
                    >
                      {renderCard(employee)}
                    </div>
                  );
                })}
              </div>
            ) : (
              items.map((employee) => renderCard(employee))
            )}
          </SortableContext>
        )}
      </div>
    </div>
  );
};

// Memoização para evitar re-renders quando outras colunas mudam
export const Column = React.memo(ColumnComponent, (prevProps, nextProps) => {
  const itemsChanged =
    prevProps.items.length !== nextProps.items.length ||
    prevProps.items.some((item, idx) => item.id !== nextProps.items[idx]?.id);

  return (
    prevProps.id === nextProps.id &&
    prevProps.title === nextProps.title &&
    prevProps.color === nextProps.color &&
    !itemsChanged &&
    prevProps.isHighlighted === nextProps.isHighlighted &&
    prevProps.isStacked === nextProps.isStacked &&
    prevProps.compactStack === nextProps.compactStack &&
    prevProps.isCollapsed === nextProps.isCollapsed &&
    prevProps.onToggleCollapse === nextProps.onToggleCollapse &&
    prevProps.onAdd === nextProps.onAdd &&
    prevProps.onEdit === nextProps.onEdit &&
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.canDragEmployees === nextProps.canDragEmployees &&
    prevProps.roleColors === nextProps.roleColors
  );
});
